/**
 * 车一整套数据：车档、五阶段组装、工位、路上事件。
 *
 * 这一层只放「数字和说法」，不动数据库，也不认识引擎；
 * 真正开工与开车在 systems/assemble.js 与 systems/vehicle.js 里。
 */

/**
 * 车档。
 *  maxSpeed   普通油门上限（像素/秒，一格 40 像素）
 *  boostSpeed 按住 Shift 的上限
 *  accel      加速度
 *  handling   转向角速度（弧度/秒）
 *  fuelRate   每格耗油量（单位：灯油）
 *  maxFuel    油箱容量
 *  maxDur     车况上限
 *  seats      能载多少（往后做货运用）
 */
export const VEHICLE_DEFS = {
  wreck: {
    id: 'wreck', name: '破车', tier: 0,
    desc: '第一辆车都这样：铁皮斑驳，跑起来浑身响，可它认路。',
    maxSpeed: 230, boostSpeed: 300, accel: 260, handling: 2.6, brake: 420,
    fuelRate: 0.22, maxFuel: 40, maxDur: 60, seats: 4,
    body: '#8a8578', accent: '#6d8fa8', roof: '#a3a093',
    upgrade: { to: 'truck', cash: 120, need: { scrap_iron: 6, bolt: 4, plate_scrap: 4, tire: 2 } },
  },
  truck: {
    id: 'truck', name: '旧货车', tier: 1,
    desc: '有车厢能装货，跑长途心里有底。',
    maxSpeed: 260, boostSpeed: 330, accel: 230, handling: 2.2, brake: 460,
    fuelRate: 0.26, maxFuel: 60, maxDur: 90, seats: 12,
    body: '#7f8a72', accent: '#a2743f', roof: '#96a189',
    upgrade: { to: 'tuned', cash: 260, need: { scrap_iron: 10, bolt: 8, plate_scrap: 8, engine: 1 } },
  },
  tuned: {
    id: 'tuned', name: '改装车', tier: 2,
    desc: '引擎重新磨过，声音沉了，油门也灵了。',
    maxSpeed: 330, boostSpeed: 430, accel: 320, handling: 2.8, brake: 560,
    fuelRate: 0.30, maxFuel: 60, maxDur: 100, seats: 6,
    body: '#6d8fa8', accent: '#d9705e', roof: '#8aa6bb',
    upgrade: { to: 'rv', cash: 480, need: { scrap_iron: 16, plate_scrap: 12, wood_scrap: 10, seat_part: 2 } },
  },
  rv: {
    id: 'rv', name: '房车', tier: 3,
    desc: '里头支了张床，走到哪儿住到哪儿。',
    maxSpeed: 290, boostSpeed: 360, accel: 200, handling: 1.8, brake: 520,
    fuelRate: 0.36, maxFuel: 90, maxDur: 140, seats: 20,
    body: '#cbb894', accent: '#8fae6b', roof: '#ded0b4',
    upgrade: { to: 'skiff', cash: 900, need: { scrap_iron: 20, plate_scrap: 16, battery: 2, jade: 3 } },
  },
  skiff: {
    id: 'skiff', name: '飞舟', tier: 4,
    desc: '不知谁把符阵嵌进了底盘，车开起来离地三寸。',
    maxSpeed: 360, boostSpeed: 480, accel: 300, handling: 3.0, brake: 620,
    fuelRate: 0.40, maxFuel: 100, maxDur: 120, seats: 10,
    body: '#9fb8c9', accent: '#d9b45b', roof: '#bcd2de', hover: true,
    upgrade: null,
  },
};

export const DEFAULT_VEHICLE_KIND = 'wreck';

/** 车的尺寸（像素）：渲染与碰撞盒都按这个来 */
export const VEHICLE_SIZE = { w: 34, h: 22 };

/**
 * 五阶段组装。
 *  need   这一段吞掉的零件
 *  soft   失败时可能损掉的东西（只有辅料，大件绝不销毁）
 *  sec    这一段耗多少游戏分钟（读条的意义就在这）
 */
export const STAGES = [
  {
    id: 'chassis', name: '底盘', tagEff: 3, min: 30,
    need: { frame: 1, tire: 4, bolt: 1 },
    soft: ['bolt', 'plate_scrap'],
    says: '车架子横在两条长凳上，四条轮胎靠墙立着。先落地的是腿。',
    done: '四轮落地，架子不再晃了。',
  },
  {
    id: 'power', name: '动力', tagEff: 4, min: 40,
    need: { engine: 1, bolt: 1, fuel: 2 },
    soft: ['bolt', 'fuel'],
    says: '引擎吊起来往下落的那一下，手心全是汗。',
    done: '引擎咳了两声，居然着了。',
  },
  {
    id: 'steer', name: '转向', tagEff: 3, min: 30,
    need: { bolt: 1, plate_scrap: 2 },
    soft: ['plate_scrap'],
    says: '拉杆、球头、一根根掰直的旧铁，凑成一副听话的方向。',
    done: '转一把方向，前轮跟着动了。',
  },
  {
    id: 'electric', name: '电气', tagEff: 4, min: 30,
    need: { battery: 1, plate_scrap: 2, wood_scrap: 2 },
    soft: ['plate_scrap', 'wood_scrap'],
    says: '线是乱的，电瓶是旧的，接错一根就得从头再来。',
    done: '车灯啪地亮了，照出一地飞蛾。',
  },
  {
    id: 'final', name: '总装', tagEff: 4, min: 40,
    need: { seat_part: 2, plate_scrap: 2, wood_scrap: 2 },
    soft: ['plate_scrap', 'wood_scrap'],
    says: '补铁皮、钉板条、把座椅摁稳——最后一遍扳手。',
    done: '整车抡了一遍扳手，它能走了。',
  },
];

export const STAGE_COUNT = STAGES.length;

/** 组装失败的基础概率，靠「手艺」与「工位」往下压 */
export const BASE_FAIL = 0.22;

/** 工具 / 工位：干活的地方不一样，手也不一样稳 */
export const BENCHES = {
  none: { id: 'none', name: '徒手拧', bonus: 0.0, desc: '只有一把随身扳手，全靠手熟。' },
  home: { id: 'home', name: '自家工作台', bonus: 0.03, desc: '顺手的工具都摆在自家案子上。' },
  forge: { id: 'forge', name: '铁砧炉火', bonus: 0.05, desc: '有砧有火，弯了的铁能掰回来。' },
  garage: { id: 'garage', name: '车铺工位', bonus: 0.08, desc: '郭大有的地沟与吊架，专业家伙事儿。' },
};

/** 场景 -> 工位 */
export const SCENE_BENCH = {
  home: 'home',
  carport: 'none',
  garage: 'garage',
};

/** 器物 type -> 工位：站到哪件家伙跟前干，就是哪档水平 */
export const BENCH_PROPS = {
  workbench: 'home',
  carpentry_workbench: 'home',
  wood_bench: 'home',
  quern: 'home',
  manual_grindstone: 'home',
  iron_anvil: 'forge',
  anvil: 'forge',
  forging_stove: 'forge',
  part_shelf: 'garage',
  tool_rack: 'garage',
  assembly_bench: 'garage',
};

/** 「机械」标签压失败率：一点一点点往下压，压到封顶为止 */
export const MACHINERY_FAIL_PER_POINT = 0.0015;
export const MACHINERY_FAIL_CAP = 0.12;

/* ------------------------------ 读条工时 ------------------------------ */

/** 一段活在脸上要拧多久：游戏分钟 -> 实机秒 */
export const WORK_SEC_PER_MIN = 0.11;
/** 再手快也得拧这么久，再笨重也不让人干等 */
export const WORK_SEC_MIN = 2;
export const WORK_SEC_MAX = 6;

/**
 * 第 stageIndex 段要读多久的条（实机秒）。
 * 工位好、手艺高，手上就快些——稳的好处已经在失手率里给过他们了。
 */
export function workSeconds(stageIndex, opts = {}) {
  const st = STAGES[stageIndex];
  if (!st) return 0;
  const bench = BENCHES[opts.benchId] || BENCHES.none;
  const base = (Number(st.min) || 0) * WORK_SEC_PER_MIN;
  const skill = Math.max(0, Number(opts.skill) || 0);
  const quick = 1 - Math.min(0.35, (bench.bonus || 0) * 2 + Math.min(20, skill) * 0.01);
  return Math.max(WORK_SEC_MIN, Math.min(WORK_SEC_MAX, +(base * quick).toFixed(2)));
}

export function stageNeed(index) {
  const st = STAGES[index];
  return st ? { ...st.need } : {};
}

/** 整车从头攒到尾一共要什么（给 UI 列单子用） */
export function totalNeed() {
  const out = {};
  for (const st of STAGES) {
    for (const [k, v] of Object.entries(st.need)) out[k] = (out[k] || 0) + v;
  }
  return out;
}

/** 从第 fromStage 段起，后面还没装的都缺什么 */
export function remainingNeed(fromStage = 0) {
  const out = {};
  for (let i = fromStage; i < STAGES.length; i++) {
    for (const [k, v] of Object.entries(STAGES[i].need)) out[k] = (out[k] || 0) + v;
  }
  return out;
}

/* ------------------------------ 路上事件 ------------------------------ */

/** 每跑够这么多格掷一次；weight 越大越常见 */
export const EVENT_EVERY_TILES = 12;

export const ROAD_EVENTS = [
  { id: 'nothing', name: '一路无事', weight: 62, text: '', effect: null },
  { id: 'rough', name: '一段碎石路', weight: 14, text: '一段碎石路，车子咯噔咯噔挨下来。', effect: { durability: -2 } },
  { id: 'pothole', name: '路面塌了一块', weight: 10, text: '路面塌了一块，车头猛地一沉。', effect: { durability: -4, speedKick: 0.4 } },
  { id: 'badFuel', name: '油里掺了假', weight: 8, text: '这一壶油不知掺了什么，引擎咳得厉害。', effect: { fuel: -3 } },
  { id: 'hitcher', name: '路边有人招手', weight: 6, text: '路边有人招手，搭了一段路，硬塞给你几个钱。', effect: { cash: [2, 9] } },
];

export function rollRoadEvent(seed = Math.random()) {
  const total = ROAD_EVENTS.reduce((s, e) => s + e.weight, 0);
  let r = seed * total;
  for (const e of ROAD_EVENTS) {
    r -= e.weight;
    if (r <= 0) return e;
  }
  return ROAD_EVENTS[0];
}

/* ------------------------------ 修 / 加油 / 推车 ------------------------------ */

/** 就地修一次（路边抡扳手）要的东西与回多少血 */
export const REPAIR_COST = { scrap_iron: 1, bolt: 1 };
export const REPAIR_GAIN = 25;

/** 一壶灯油加多少油量 */
export const FUEL_PER_CAN = 10;

/** 车况低于这个数说话就难听了，到 0 就是瘫在路上 */
export const BROKEN_LINE = 0;

/** 车瘫了推着走的速度（像素/秒） */
export const PUSH_SPEED = 46;
